"use client";

import log from "logbeacon";
import { deviceDetector } from "@ospweb/sdk-ui";
import { useMemberUser } from "./useMemberUser";
import {
  openPlaidImportModal,
  // openPortfolioAccountsModal,
} from "@/src/components/PlaidImportModal";
import type { PlaidBindSuccessContext } from "@/src/components/PlaidImportModal/types";
import { favoritesCenter } from "@/src/store/favorite/FavoritesCenter";
import { OnboardingPaywallModalCallable } from "@/src/components/OnboardingWizard/Paywall/OnboardingPaywallModalCallable";
import { useMemoizedFn } from "ahooks";
import { OnboardingPaywallFreeTrialContentProps } from "@components/OnboardingWizard/Paywall";
import {
  usePlaidGateRemoteConfig,
  usePlaidGateStatus,
  shouldRequirePlaidConnectPaywall,
} from "@/src/hooks/plaid";

export interface HandleImportClickParams {
  /**
   * Skips the manage modal and opens Plaid import directly. Used from the
   * AddAccount drawer "Connect with Plaid" entry where the user already has
   * existing connections — prevents a dead-loop back to the manage modal.
   */
  forceFreshConnect?: boolean;
  /** Per-call Paywall copy（如 Accounts Reconnect）；覆盖 hook 级 defaults */
  paywallCopyOverrides?: OnboardingPaywallFreeTrialContentProps["paywallCopyOverrides"];
  /** Per-call analytics source */
  analyticsSource?: string;
  /** Per-call tracking module */
  trackingModuleName?: string;
}

export interface UseHandleImportClickOptions {
  /** Runs after built-in folder refresh on Plaid bind success. */
  onBindSuccess?: (ctx: PlaidBindSuccessContext) => void | Promise<void>;
  /** Runs when user closes the Plaid import modal. */
  onComplete?: () => void;
  paywallCopyOverrides?: OnboardingPaywallFreeTrialContentProps["paywallCopyOverrides"];
}

export interface UseHandleImportClickReturn {
  handleImportClick: (params?: HandleImportClickParams) => Promise<void>;
}

export const useHandleImportClick = (
  options?: UseHandleImportClickOptions,
): UseHandleImportClickReturn => {
  const { onBindSuccess, onComplete, paywallCopyOverrides } = options || {};
  const { data: memberPlanInfo, isLoading: isMemberLoading } = useMemberUser();
  const { mode: gateMode } = usePlaidGateRemoteConfig();
  const {
    data: gate,
    isLoading: isGateLoading,
    isError: isGateError,
  } = usePlaidGateStatus({ enabled: true });

  const handleImportClick = useMemoizedFn(
    async (params?: HandleImportClickParams) => {
      if (isMemberLoading || isGateLoading) {
        log.debug(
          "[useHandleImportClick] Member/gate loading, skipping action",
        );
        return;
      }

      // S1.4：门控态读取失败 → 不放行也不弹墙
      if (isGateError || gate === undefined) {
        log.debug(
          "[useHandleImportClick] Gate status unavailable, aborting connect",
        );
        return;
      }

      const isPaidMember = Boolean(memberPlanInfo?.isMember);
      const requirePaywall = shouldRequirePlaidConnectPaywall({
        isPaidMember,
        gateMode,
        plaidStatus: gate.plaidStatus,
      });

      if (requirePaywall) {
        try {
          const paywallPaid = await OnboardingPaywallModalCallable({
            isPC: !deviceDetector.isMobile(),
            analyticsSource: params?.analyticsSource ?? "portfolio_connect",
            trackingModuleName:
              params?.trackingModuleName ?? "portfolio_connect",
            paywallCopyOverrides:
              params?.paywallCopyOverrides ?? paywallCopyOverrides,
          });
          if (!paywallPaid) {
            log.debug(
              "[useHandleImportClick] Paywall dismissed, aborting connect",
            );
            return;
          }
        } catch (error) {
          log.error("[useHandleImportClick] Paywall modal error:", error);
          return;
        }
      } else {
        log.debug(
          "[useHandleImportClick] Skip paywall (free_trial unused or paid)",
          { gateMode, plaidStatus: gate.plaidStatus, isPaidMember },
        );
      }

      log.debug("[useHandleImportClick] Opening portfolio Plaid modal");
      try {
        const bindSuccessHandler = async (ctx: PlaidBindSuccessContext) => {
          log.debug(
            "[useHandleImportClick] Plaid bind success, refreshing folders",
          );
          await favoritesCenter.fetchUserFolders({ forceRefresh: true });
          await onBindSuccess?.(ctx);
        };

        const result = await openPlaidImportModal({
          mode: "default",
          onBindSuccess: bindSuccessHandler,
        });

        if (result.action === "close") {
          onComplete?.();
        }
      } catch (error) {
        log.error("[useHandleImportClick] Plaid import modal error:", error);
      }
    },
  );

  return {
    handleImportClick,
  };
};
